import React from 'react';
import {
  Modal,
  Dialogue,
  Button,
  Text,
  Heading,
  LoadingSpinner,
  Icon,
  addToast
} from '@albert-io/atomic';
import {history} from 'client/history';
import {normalizeError} from 'lib/normalizeError';

import {withRouter} from 'react-router';

import {useShallow} from 'zustand/react/shallow';

import type {List} from 'immutable';
import type {QuestionSetModelV1} from '@albert-io/models';

import {useContentDiscoveryStore} from '../ContentDiscovery/store';
import {getAssignmentQuestionSetsQuery} from '../ContentDiscovery/ViewAssignment/ViewAssignment.queries';

type Props = {
  assignmentId: string;
  assignmentName?: string;
  handleClose: () => void;
  handleReassign?: () => void;
  router: any;
};

const handleReassignDefault = ({router}) => {
  router.push(`/assignment-builder/create-assignment/settings`);
};

const CustomizeReassignModal = ({
  assignmentId,
  assignmentName,
  handleClose,
  handleReassign,
  router
}: Props) => {
  const [isLoading, setIsLoading] = React.useState(false);
  const [hasError, setHasError] = React.useState(false);
  const {setSelectedQuestionSets, setIsReassign, setDraftName} = useContentDiscoveryStore(
    useShallow((state) => ({
      setSelectedQuestionSets: state.draft.setSelectedQuestionSets,
      setIsReassign: state.assignment.setIsReassign,
      setDraftName: state.draft.setDraftName
    }))
  );

  const fetchAndSetQuestionSets = async () => {
    setIsLoading(true);
    setHasError(false);
    try {
      // Invalidate first so a retry re-fetches instead of resolving the empty cached interest a prior
      const query = getAssignmentQuestionSetsQuery(assignmentId, false, true);
      query.invalidateInterest();
      const questions = (await query.getResourcePromise()) as List<QuestionSetModelV1>;
      setDraftName(`${assignmentName} (Reassigned)`);
      setIsReassign(true);
      setSelectedQuestionSets(questions);
      if (handleReassign) {
        handleReassign();
      } else {
        handleReassignDefault({router});
      }
      handleClose();
    } catch (error) {
      // Keep the modal open + reset loading so the teacher can retry
      setHasError(true);
      setIsLoading(false);
      addToast({
        color: 'negative',
        title: "Couldn't start reassign",
        message: normalizeError(error).message
      });
    }
  };

  return (
    <Modal ariaLabel='customize settings' handleClose={!isLoading ? handleClose : () => {}}>
      {({modalContentStyle}) => (
        <Dialogue
          inModal
          handleClose={!isLoading ? handleClose : () => {}}
          className={modalContentStyle}
          size='m'
        >
          <div className='flex flex-col items-center justify-center'>
            <Heading className='mt-x4' size='xl'>
              <Icon icon='info-circle' iconStyle='light' />
            </Heading>
            <Text bold className='mt-x5 text-center text-pretty'>
              Trying to adjust settings for specific students?
            </Text>
            <Text className='mt-x2 text-center text-pretty'>
              You can now customize time, due dates, and more for individual students by editing the
              assignment.
            </Text>
            {isLoading ? (
              <LoadingSpinner size={3} className='mt-x5' />
            ) : hasError ? (
              <>
                <Text color='negative' className='mt-x5 mb-x1 text-center text-pretty'>
                  We couldn’t reassign this assignment. Please try again.
                </Text>
                <Button
                  variant='solid'
                  size='m'
                  className='mb-x1'
                  onClick={fetchAndSetQuestionSets}
                  data-testid='reassign-modal_retry-button'
                >
                  Try Again
                </Button>
                <Button variant='text' size='m' onClick={handleClose}>
                  Cancel
                </Button>
              </>
            ) : (
              <>
                <Button
                  variant='text'
                  size='m'
                  className='mt-x5 mb-x1'
                  onClick={() => {
                    history.pushState(
                      null,
                      `/assignment-builder/create-assignment/${assignmentId}/settings`
                    );
                    handleClose();
                  }}
                  data-testid='reassign-modal_edit-settings-button'
                >
                  Edit Assignment Settings
                </Button>
                <Button
                  size='m'
                  onClick={fetchAndSetQuestionSets}
                  data-testid='reassign-modal_reassign-button'
                >
                  Continue to Reassign
                </Button>
              </>
            )}
          </div>
        </Dialogue>
      )}
    </Modal>
  );
};

export default withRouter(CustomizeReassignModal);
